import React, { useState, useEffect, useRef } from 'react';
import { User, UpdateUserRequest } from '../types';
import userService from '../services/userService';
import { FadeLoader } from 'react-spinners';
import '../styles/UserManagement.css';

interface EditUserFormProps {
  user: User;
  onUserUpdated: () => void;
  onCancel: () => void;
}

export const EditUserForm: React.FC<EditUserFormProps> = ({ user, onUserUpdated, onCancel }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<UpdateUserRequest>({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
    gender: user.gender || 'prefer-not-to-say',
    role: user.role,
    permissions: user.permissions || [],
    notificationPreferences: user.notificationPreferences || {
      email: true,
      push: false,
      sms: false
    },
    phoneNumber: user.phoneNumber || '',
    department: user.department || '',
    employeeId: user.employeeId || '',
    address: user.address || {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    notes: user.notes || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle escape key and click outside to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [onCancel]);

  const getAvailablePermissions = () => [
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'user-management', label: 'User Management' },
    { value: 'demographics', label: 'Demographics' },
     { value: 'product-management', label: 'Product Management' },
    { value: 'orders', label: 'Orders' },
    { value: 'invoices', label: 'Invoices' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'settings', label: 'Settings' }
  ];

  const getRolePermissions = (role: string) => {
    const rolePermissions: Record<string, string[]> = {
      admin: ['settings', 'configuration', 'user-management'],
      manufacturer: ['settings', 'invoices', 'orders', 'product-management'],
      practitioner: ['settings', 'demographics', 'orders', 'insurance'],
      insurance: ['settings', 'insurance']
    };
    return rolePermissions[role] || [];
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Auto-set permissions based on role
    if (name === 'role') {
      const rolePermissions = getRolePermissions(value);
      setFormData(prev => ({
        ...prev,
        role: value as any,
        permissions: rolePermissions
      }));
    }
  };

  const handlePermissionChange = (permission: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, permission]
        : prev.permissions.filter(p => p !== permission)
    }));
  };

  const handleNotificationChange = (type: 'email' | 'push' | 'sms', checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      notificationPreferences: {
        ...prev.notificationPreferences,
        [type]: checked
      }
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (formData.phoneNumber && formData.phoneNumber.trim()) {
      const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
      if (!phoneRegex.test(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Please enter a valid phone number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await userService.updateUser(user._id, formData);
      onUserUpdated();
    } catch (error) {
      console.error('Error updating user:', error);
      setErrors({ submit: 'Failed to update user. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className={`modal-content ${isSubmitting ? 'loading' : ''}`} ref={modalRef}>
        <div className="modal-header">
          <h2>Edit User: {user.firstName} {user.lastName}</h2>
          <button 
            className="close-button" 
            onClick={onCancel}
            disabled={isSubmitting}
            title="Close modal"
          >
            ✕
          </button>
        </div>
        
        {isSubmitting && (
          <div className="modal-loading" style={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            padding: '2rem'
          }}>
            <FadeLoader 
              color="#1976D2" 
              loading={true} 
              height={15} 
              width={5} 
              radius={2} 
              margin={2}
              speedMultiplier={0.8}
            />
            <p style={{ marginTop: '1rem', color: '#666' }}>Updating user...</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="user-form">
          {errors.submit && (
            <div className="error-message">{errors.submit}</div>
          )}

          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={errors.firstName ? 'error' : ''}
                />
                {errors.firstName && <span className="error-message">{errors.firstName}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={errors.lastName ? 'error' : ''}
                />
                {errors.lastName && <span className="error-message">{errors.lastName}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dateOfBirth">Date of Birth</label>
                <input
                  type="date"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="gender">Gender</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                >
                  <option value="prefer-not-to-say">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Role and Permissions */}
          <div className="form-section">
            <h3>Role & Permissions</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="role">Role *</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className={errors.role ? 'error' : ''}
                >
                  <option value="">Select a role</option>
                  <option value="admin">Admin</option>
                  <option value="manufacturer">Manufacturer</option>
                  <option value="practitioner">Practitioner</option>
                  <option value="insurance">Insurance</option>
                </select>
                {errors.role && <span className="error-message">{errors.role}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Permissions</label>
              <div className="permissions-options">
                {getAvailablePermissions().map((permission) => (
                  <div key={permission.value} className="permission-option">
                    <input
                      type="checkbox"
                      id={`permission-${permission.value}`}
                      checked={formData.permissions.includes(permission.value)}
                      onChange={(e) => handlePermissionChange(permission.value, e.target.checked)}
                    />
                    <label htmlFor={`permission-${permission.value}`}>{permission.label}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="form-section">
            <h3>Contact Information</h3>
            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="+1 (555) 123-4567"
                className={errors.phoneNumber ? 'error' : ''}
              />
              {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="department">Department</label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="employeeId">Employee ID</label>
                <input
                  type="text"
                  id="employeeId"
                  name="employeeId"
                  value={formData.employeeId}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="form-section">
            <h3>Address</h3>
            <div className="form-group">
              <label htmlFor="street">Street Address</label>
              <input
                type="text"
                id="street"
                name="address.street"
                value={formData.address.street}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  address: { ...prev.address, street: e.target.value }
                }))}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  name="address.city"
                  value={formData.address.city}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, city: e.target.value }
                  }))}
                />
              </div>

              <div className="form-group">
                <label htmlFor="state">State/Province</label>
                <input
                  type="text"
                  id="state"
                  name="address.state"
                  value={formData.address.state}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, state: e.target.value }
                  }))}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="zipCode">ZIP/Postal Code</label>
                <input
                  type="text"
                  id="zipCode"
                  name="address.zipCode"
                  value={formData.address.zipCode}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, zipCode: e.target.value }
                  }))}
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">Country</label>
                <input
                  type="text"
                  id="country"
                  name="address.country"
                  value={formData.address.country}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, country: e.target.value }
                  }))}
                />
              </div>
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="form-section">
            <h3>Notification Preferences</h3>
            <div className="notification-preferences">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.notificationPreferences.email}
                  onChange={(e) => handleNotificationChange('email', e.target.checked)}
                />
                <span className="checkbox-text">Email Notifications</span>
              </label>
              <p className="setting-description">Receive important updates and notifications via email</p>
            </div>
          </div>

          {/* Additional Information */}
          <div className="form-section">
            <h3>Additional Information</h3>
            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Any additional notes about this user..."
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-primary btn-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
